<template>
  <teleport to="body">
    <transition
      name="modal-fade"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="isVisible"
        class="makercart-modal-overlay"
        @click.self="closeModal"
      >
        <div class="makercart-modal-container container-xxl">
          <!-- Loading State -->
          <div v-if="loading" class="modal-loading-overlay">
            <div class="modal-loading-content">
              <div class="modal-loading-spinner"></div>
              <div class="modal-loading-text">Loading activity details...</div>
              <div class="modal-loading-subtitle">
                Please wait while we fetch the information
              </div>
            </div>
          </div>

          <!-- Main Modal Content -->
          <div v-else class="modal-main-content">
            <button
              type="button"
              class="makercart-modal-close"
              aria-label="Close activity details"
              @click="closeModal"
            >
              <CloseCircleIcon color="#3B4648" />
            </button>
            <!-- Main Content -->
            <div class="makercart-modal-content-container">
              <!-- Header -->
              <div class="makercart-content-header">
                <!-- Resource Views Banner -->
                <div class="resource-views-banner" v-if="!isAuthenticated">
                  <span class="banner-text">
                    You have
                    <span class="remaining-count">{{ limitResourceLeft }}</span
                    >/3 resource views remaining.
                    <button
                      type="button"
                      class="login-signup-btn"
                      @click="openLoginModal"
                    >
                      Sign In or Sign Up
                    </button>
                    for free to unlock full access!
                  </span>
                </div>

                <div class="makercart-modal-title-container">
                  <div class="d-flex flex-column align-start mb-3">
                    <h1 class="makercart-modal-title">
                      {{ makerCartData.makercart_activity || "Activity Title" }}
                    </h1>
                    <span class="makercart-modal-subtitle">{{
                      makerCartData.subtitle || "Subtitle"
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="mobile-makercart-modal-close"
                    aria-label="Close activity details"
                    @click="closeModal"
                  >
                    <CloseCircleIcon width="24" height="24" color="#3B4648" />
                  </button>
                </div>

                <div
                  class="main-description formatted-description"
                  v-html="
                    formatAirtableText(
                      makerCartData.description || 'No description available.'
                    )
                  "
                ></div>

                <!-- Divider -->
                <div class="main-description-divider"></div>

                <!-- Activity Tabs -->
                <div
                  class="activity-tabs-container"
                  v-if="currentActivities.length > 0"
                >
                  <!-- Desktop Tabs -->
                  <div class="activity-tabs" v-if="!isMobile">
                    <button
                      type="button"
                      v-for="activity in currentActivities"
                      :key="activity.id"
                      :class="[
                        'activity-tab',
                        { active: activeActivityTab === activity.id },
                      ]"
                      @click="activeActivityTab = activity.id"
                    >
                      {{ activity.makercart_activity }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="makercart-modal-content-content">
                <!-- Left Section: Image Gallery -->
                <div class="makercart-gallery-section">
                  <div
                    class="main-image-container"
                    @click="openImageGalleryWithIndex(currentImageIndex)"
                  >
                    <img
                      :src="currentImage"
                      :alt="makerCartData.makercart_activity"
                      class="main-image"
                      @error="handleImageError"
                    />
                  </div>
                  <!-- Desktop Thumbnail Container -->
                  <div class="thumbnail-container" v-if="!isMobile">
                    <div
                      v-for="(image, index) in displayImages"
                      :key="index"
                      class="thumbnail-wrapper"
                      :class="{ active: index === currentImageIndex }"
                      @click="setCurrentImage(index)"
                    >
                      <img
                        :src="image"
                        :alt="`${makerCartData.makercart_activity} - Image ${index + 1}`"
                        class="thumbnail-image"
                        @error="handleImageError"
                      />
                    </div>
                    <button
                      v-if="hasMoreImages"
                      type="button"
                      class="view-more-btn"
                      @click="showImageGallery"
                    >
                      <span>View More</span>
                      <ArrowShortRightIcon
                        width="28"
                        height="28"
                        color="#00925e"
                      />
                    </button>
                  </div>

                  <!-- Mobile Navigation Arrows -->
                  <div
                    v-if="isMobile && allImages.length > 1"
                    class="mobile-image-nav"
                  >
                    <button
                      type="button"
                      class="mobile-nav-btn mobile-nav-btn-left"
                      aria-label="Show previous image"
                      @click="previousImage"
                      :disabled="currentImageIndex === 0"
                    >
                      <ChevronLeftIcon width="16" height="16" color="#00925e" />
                    </button>
                    <button
                      type="button"
                      class="mobile-nav-btn mobile-nav-btn-right"
                      aria-label="Show next image"
                      @click="nextImage"
                      :disabled="currentImageIndex === allImages.length - 1"
                    >
                      <ChevronRightIcon
                        width="16"
                        height="16"
                        color="#00925e"
                      />
                    </button>
                  </div>
                </div>

                <div class="signature-makercart-mobile-section">
                  <div
                    class="mobile-section-description formatted-description"
                    v-html="formatAirtableText(makerCartData.description)"
                  ></div>
                  <!-- Mobile Activity Selector -->
                  <div
                    class="mobile-activity-selector"
                    v-if="isMobile && currentActivities.length > 0"
                  >
                    <div class="mobile-activity-tabs-container">
                      <div class="mobile-activity-tabs">
                        <button
                          type="button"
                          v-for="activity in currentActivities"
                          :key="activity.id"
                          :class="[
                            'mobile-activity-tab',
                            { active: activeActivityTab === activity.id },
                          ]"
                          @click="activeActivityTab = activity.id"
                        >
                          {{ truncateWords(activity.makercart_activity, 5) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Center Section: Content -->
                <div class="makercart-content-section">
                  <!-- Current Activity Title -->
                  <h2 class="current-activity-title" v-if="currentActivity">
                    {{ currentActivity.makercart_activity }}
                  </h2>

                  <!-- Badges -->
                  <div class="badges-container">
                    <Badge
                      :variant="
                        makerCartData.series === 'TrendX'
                          ? 'purple'
                          : 'yellow-light'
                      "
                      :label="makerCartData.series"
                    />
                  </div>

                  <!-- Tags -->
                  <div class="tags-container">
                    <div
                      v-for="(tag, idx) in visibleTags"
                      :key="`${tag}-${idx}`"
                      class="tag-button"
                    >
                      {{ tag }}
                    </div>
                    <div
                      v-if="remainingTagsCount > 0 || showAllTags"
                      class="tag-button tag-button-more clickable"
                      :class="
                        showAllTags ? 'tag-button-less' : 'tag-button-more'
                      "
                      @click="toggleAllTags"
                      @keydown.enter="toggleAllTags"
                      @keydown.space.prevent="toggleAllTags"
                      role="button"
                      tabindex="0"
                    >
                      {{
                        showAllTags
                          ? "View Less"
                          : `+ ${remainingTagsCount} more`
                      }}
                    </div>
                  </div>

                  <!-- Divider -->
                  <div class="divider"></div>

                  <!-- Overview and Lesson Outcomes -->
                  <div class="content-sections">
                    <div class="content-column">
                      <h3 class="section-title">Overview</h3>
                      <div
                        class="section-content formatted-description"
                        v-html="
                          formatAirtableText(
                            currentActivity?.activity_description ||
                              makerCartData.activity_description ||
                              'No description available.'
                          )
                        "
                      ></div>
                    </div>
                    <div class="content-column">
                      <h3 class="section-title">Lesson Outcomes</h3>
                      <div class="section-content">
                        <div
                          class="formatted-outcomes"
                          v-html="
                            formatLearningOutcomes(
                              currentActivity?.learning_outcomes ||
                                makerCartData.learning_outcomes ||
                                ''
                            )
                          "
                        ></div>
                      </div>
                    </div>
                  </div>

                  <!-- Lesson Materials Button -->
                  <CustomTooltip
                    placement="top"
                    :text="currentActivity?.note || ''"
                    v-if="
                      (currentActivity?.is_disabled ||
                        !currentActivity?.public_visibility) &&
                      currentActivity?.note
                    "
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="sample-materials-btn"
                        :disabled="
                          currentActivity?.is_disabled ||
                          !currentActivity?.public_visibility
                        "
                        @click="viewLessonMaterials"
                      >
                        <ExternalLinkIcon width="18" height="18" />
                        Lesson Materials
                      </button>
                    </template>
                  </CustomTooltip>
                  <button
                    v-else
                    type="button"
                    class="sample-materials-btn"
                    :disabled="
                      currentActivity?.is_disabled ||
                      !currentActivity?.public_visibility
                    "
                    @click="viewLessonMaterials"
                  >
                    <ExternalLinkIcon width="18" height="18" />
                    Lesson Materials
                  </button>

                  <!-- Divider -->
                  <div class="divider"></div>

                  <!-- Expandable Details Section -->
                  <div class="details-section">
                    <button
                      type="button"
                      class="details-toggle"
                      @click="toggleDetails"
                      :class="{ expanded: showDetails }"
                    >
                      <span>{{
                        showDetails ? "Hide Details" : "View Details"
                      }}</span>
                      <ChevronDownIcon width="20" height="20" />
                    </button>

                    <transition name="details-expand">
                      <div v-if="showDetails" class="details-content">
                        <div class="details-grid">
                          <!-- Materials Provided -->
                          <div class="detail-column">
                            <div class="detail-title-with-icon">
                              <CustomTooltip
                                placement="top"
                                text="Each cart is packed with enough materials to run your chosen activities—scaled to your selected student count."
                              >
                                <template #trigger>
                                  <img
                                    src="/images/icon/i-circle-icon.svg"
                                    style="margin-bottom: 8px"
                                    alt=""
                                  />
                                </template>
                              </CustomTooltip>
                              <h4 class="detail-title">Materials provided</h4>
                            </div>
                            <ul class="detail-list">
                              <li
                                v-if="loadingMaterials"
                                class="loading-materials"
                              >
                                Loading materials...
                              </li>
                              <li
                                v-else
                                v-for="material in materialsProvided"
                                :key="material"
                              >
                                {{ material }}
                              </li>
                              <li
                                v-if="
                                  !loadingMaterials &&
                                  materialsProvided.length === 0
                                "
                              >
                                No materials provided for this activity
                              </li>
                            </ul>
                          </div>

                          <!-- You'll need to provide -->
                          <div class="detail-column border-column">
                            <div class="detail-title-with-icon">
                              <CustomTooltip
                                placement="top"
                                text="These items are not provided as part of the Makercart order."
                              >
                                <template #trigger>
                                  <img
                                    src="/images/icon/i-circle-icon.svg"
                                    style="margin-bottom: 8px"
                                    alt=""
                                  />
                                </template>
                              </CustomTooltip>
                              <h4 class="detail-title">
                                You'll need to provide
                              </h4>
                            </div>
                            <ul class="detail-list">
                              <li
                                v-if="loadingMaterials"
                                class="loading-materials"
                              >
                                Loading materials...
                              </li>
                              <li
                                v-else
                                v-for="material in materialsToProvide"
                                :key="material"
                              >
                                {{ material }}
                              </li>
                              <li
                                v-if="
                                  !loadingMaterials &&
                                  materialsToProvide.length === 0
                                "
                              >
                                No additional materials needed for this activity
                              </li>
                            </ul>
                          </div>

                          <!-- Required Equipment -->
                          <div class="detail-column border-column">
                            <h4 class="detail-title">Required Equipment</h4>
                            <ul class="detail-list">
                              <li
                                v-for="equipment in currentActivity?.hardware_associated ||
                                makerCartData.hardware_associated ||
                                []"
                                :key="equipment"
                              >
                                {{ equipment }}
                              </li>
                              <li
                                v-if="
                                  (
                                    currentActivity?.hardware_associated ||
                                    makerCartData.hardware_associated ||
                                    []
                                  ).length === 0
                                "
                              >
                                No special equipment required
                              </li>
                            </ul>
                          </div>
                        </div>
                        <!-- Standards -->
                        <div class="detail-column detail-column-wide">
                          <h4 class="detail-title">Standards</h4>
                          <div class="detail-list">
                            {{
                              currentActivity?.standards ||
                              makerCartData.standards ||
                              "N/A"
                            }}
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Footer (Sticky) -->
            <div v-if="isMobile" class="mobile-modal-footer">
              <button
                type="button"
                class="mobile-add-to-cart-btn"
                @click="toggleMobileSidebar"
              >
                <ShoppingCartIcon width="20" height="20" color="#ffff" />
                Add to Cart
              </button>
            </div>

            <!-- Right Section: Selection Sidebar -->
            <div class="makercart-sidebar">
              <div class="sidebar-content">
                <h3 class="sidebar-title">Select Options</h3>
                <div class="sidebar-divider"></div>

                <!-- Number of Students -->
                <div class="form-group">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="We'll pack materials based on the selected number of projects. You can increase the number of projects by multiples of 10"
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Number of projects</label>
                  </div>
                  <div class="number-input">
                    <button
                      type="button"
                      class="number-btn"
                      aria-label="Decrease number of projects"
                      @click="decrementStudents"
                      :disabled="selectedProjects <= 80"
                    >
                      <MinusIcon width="16" height="16" />
                    </button>
                    <input
                      type="number"
                      role="spinbutton"
                      class="number-input-field"
                      v-model.number="projectInput"
                      min="80"
                      max="150"
                      step="10"
                      inputmode="numeric"
                      aria-label="Number of projects"
                      :aria-valuenow="projectInput"
                      aria-valuemin="80"
                      aria-valuemax="150"
                      @input="handleProjectsInput"
                      @blur="snapProjects"
                    />
                    <button
                      type="button"
                      class="number-btn"
                      aria-label="Increase number of projects"
                      @click="incrementStudents"
                      :disabled="selectedProjects >= 150"
                    >
                      <PlusIcon width="16" height="16" />
                    </button>
                  </div>
                  <div class="checkbox-container">
                    <CustomCheckbox
                      v-model="selectedPoster"
                      label="Add Poster (+$50)"
                      sublabel="(Poster guide to use makercart)"
                    />
                  </div>
                </div>

                <!-- Grade Selection -->
                <div class="form-group">
                  <div class="form-label-with-icon">
                    <CustomTooltip
                      placement="top"
                      text="We'll customize your lesson materials based on grade"
                    >
                      <template #trigger>
                        <img
                          src="/images/icon/i-circle-icon.svg"
                          style="margin-bottom: 4px"
                          alt=""
                        />
                      </template>
                    </CustomTooltip>
                    <label class="form-label">Choose Grade</label>
                  </div>
                  <div class="radio-group">
                    <label
                      v-for="grade in availableGrades"
                      :key="grade.value"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :value="grade.value"
                        v-model="selectedGrade"
                        class="radio-input"
                      />
                      <span class="radio-custom"></span>
                      <span class="radio-label">{{ grade.text }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="sidebar-actions">
                <div class="action-divider"></div>
                <div class="action-buttons">
                  <ActionButton
                    variant="primary"
                    :loading="isCartLoading"
                    full-width
                    @click="handleCartSelection('default')"
                  >
                    Add to Cart
                    <template #icon-right>
                      <ShoppingCartIcon width="20" height="20" color="#ffff" />
                    </template>
                  </ActionButton>

                  <ActionButton
                    variant="ghost"
                    @click="shareActivity"
                    full-width
                  >
                    <template #icon-left>
                      <ShareIcon width="20" height="20" />
                    </template>
                    Share
                  </ActionButton>
                </div>
              </div>
            </div>

            <!-- Mobile Bottom Sheet -->
            <div
              v-if="isMobile && showMobileSidebar"
              class="mobile-sidebar-backdrop"
              @click="closeMobileSidebar"
            ></div>

            <div
              v-if="isMobile"
              class="mobile-bottom-sheet"
              :class="{ show: showMobileSidebar }"
            >
              <div class="mobile-bottom-sheet-content">
                <div class="mobile-bottom-sheet-header">
                  <h3 class="mobile-bottom-sheet-title">Select Options</h3>
                  <button
                    type="button"
                    class="mobile-bottom-sheet-close"
                    aria-label="Close options sheet"
                    @click="closeMobileSidebar"
                  >
                    <img src="/images/icon/x-circle-icon.svg" alt="Close" />
                  </button>
                </div>

                <div class="mobile-bottom-sheet-body">
                  <!-- Number of Students -->
                  <div class="mobile-form-group">
                    <div class="mobile-form-label-with-icon">
                      <CustomTooltip
                        placement="top"
                        text="We'll pack materials based on the selected number of projects. You can increase the number of projects by multiples of 10"
                      >
                        <template #trigger>
                          <img
                            src="/images/icon/i-circle-icon.svg"
                            style="margin-bottom: 4px"
                            alt=""
                          />
                        </template>
                      </CustomTooltip>
                      <label class="mobile-form-label"
                        >Number of projects</label
                      >
                    </div>
                    <div class="mobile-number-input">
                      <button
                        type="button"
                        class="mobile-number-btn"
                        aria-label="Decrease number of projects"
                        @click="decrementStudents"
                        :disabled="selectedProjects <= 80"
                      >
                        <MinusIcon width="16" height="16" />
                      </button>
                      <input
                        type="number"
                        role="spinbutton"
                        class="mobile-number-input-field"
                        v-model.number="projectInput"
                        min="80"
                        max="150"
                        step="10"
                        inputmode="numeric"
                        aria-label="Number of projects"
                        :aria-valuenow="projectInput"
                        aria-valuemin="80"
                        aria-valuemax="150"
                        @input="handleProjectsInput"
                        @blur="snapProjects"
                      />
                      <button
                        type="button"
                        class="mobile-number-btn"
                        aria-label="Increase number of projects"
                        @click="incrementStudents"
                        :disabled="selectedProjects >= 150"
                      >
                        <PlusIcon width="16" height="16" />
                      </button>
                    </div>
                    <div class="mobile-checkbox-container">
                      <CustomCheckbox
                        v-model="selectedPoster"
                        label="Add Poster"
                      />
                    </div>
                  </div>

                  <!-- Grade Selection -->
                  <div class="mobile-form-group">
                    <div class="mobile-form-label-with-icon">
                      <CustomTooltip
                        placement="top"
                        text="We'll customize your lesson materials based on grade"
                      >
                        <template #trigger>
                          <img
                            src="/images/icon/i-circle-icon.svg"
                            style="margin-bottom: 4px"
                            alt=""
                          />
                        </template>
                      </CustomTooltip>
                      <label class="mobile-form-label">Choose Grade</label>
                    </div>
                    <div class="mobile-radio-group">
                      <label
                        v-for="grade in availableGrades"
                        :key="grade.value"
                        class="mobile-radio-option"
                      >
                        <input
                          type="radio"
                          :value="grade.value"
                          v-model="selectedGrade"
                          class="mobile-radio-input"
                        />
                        <span class="mobile-radio-custom"></span>
                        <span class="mobile-radio-label">{{ grade.text }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Mobile Action Buttons -->
                <div class="mobile-bottom-sheet-actions">
                  <div class="mobile-action-divider"></div>
                  <div class="mobile-action-buttons">
                    <ActionButton
                      variant="primary"
                      :loading="isCartLoading"
                      full-width
                      @click="handleCartSelection('default')"
                    >
                      <template #icon-left>
                        <ShoppingCartIcon
                          width="20"
                          height="20"
                          color="#ffff"
                        />
                      </template>
                      Add to Cart
                    </ActionButton>

                    <ActionButton
                      variant="ghost"
                      @click="shareActivity"
                      full-width
                    >
                      <template #icon-left>
                        <ShareIcon width="20" height="20" />
                      </template>
                      Share
                    </ActionButton>
                  </div>
                </div>
              </div>
            </div>

            <ChatbotAssistant
              :position="isMobile ? 'right' : 'left'"
              page-name="Activity Card"
              :activity-name="makerCartData.makercart_activity"
            />
          </div>
        </div>
      </div>
    </transition>

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      ref="galleryModalRef"
      :visible="showImageGalleryModal"
      :images="allImages"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />

    <AuthModal
      :is-open="showAuthModal"
      initial-view="login"
      @close="handleCloseAuthModal"
      @login-success="handleLoginSuccess"
      :z-index="100000000"
    />
  </teleport>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import ActionButton from "@/components/common/ActionButton.vue";
import Badge from "@/components/common/Badge.vue";
import CustomCheckbox from "@/components/common/CustomCheckbox.vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  MinusIcon,
  PlusIcon,
  ShareIcon,
  ShoppingCartIcon,
  CloseCircleIcon,
} from "@/components/icons";
import ImageGalleryModal from "@/components/maker-cart/ImageGalleryModal.vue";
import AuthModal from "@/components/modal/AuthModal.vue";
import { useCart } from "@/composeables/useCart";
import cookiesService from "@/services/cookiesService";
import makercartActivityService from "@/services/makercartActivityService";
import signatureMakercartService from "@/services/signatureMakercartService";
import stemStudioService from "@/services/stemStudioService";
import { useLocaleStore } from "@/store/locale";
import {
  computed,
  defineEmits,
  defineExpose,
  defineProps,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import ChatbotAssistant from "@/components/maker-cart/ChatbotAssistant.vue";
import { truncateWords } from "@/utils/index.js";
import {
  useMakerCart,
  useMakercartTags,
  formatAirtableText,
} from "../useMakerCart";
import ArrowShortRightIcon from "@/components/icons/ArrowShortRightIcon.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  makercartId: {
    type: [String, Number],
    default: null,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  cartId: {
    type: [String, Number],
    default: null,
  },
  activityData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close", "add-to-cart", "saved"]);

// Composables
const { isCartLoading, addSignatureMakercart, isSignatureMakercartInCart } =
  useCart();
const notify = inject("notify");
const store = useStore();
const localeStore = useLocaleStore();

// Reactive data
const isVisible = ref(false);
const makerCartData = ref({});
const loading = ref(false);
const showDetails = ref(false);
const currentImageIndex = ref(0);
const selectedProjects = ref(80);
const projectInput = ref(80);
let snapTimer = null;
const selectedGrade = ref("");
const selectedPoster = ref(false);

const { formatLearningOutcomes } = useMakerCart();

// Image Gallery Modal State
const showImageGalleryModal = ref(false);
const galleryModalRef = ref(null);
const galleryInitialIndex = ref(0);

const materialsProvided = ref([]);
const materialsToProvide = ref([]);
const loadingMaterials = ref(false);

const isAuthenticated = ref(false);
const limitResourceLeft = ref(3);
const viewedActivityIds = ref([]);
const showAuthModal = ref(false);
const activeActivityTab = ref("solar-sizzle");
const isMobile = ref(false);
const showMobileSidebar = ref(false);

// Use tags composable
const {
  showAllTags,
  allTags,
  visibleTags,
  remainingTagsCount,
  toggleAllTags,
  resetTags,
} = useMakercartTags(
  computed(() => currentActivity.value || makerCartData.value),
  isMobile
);

// Computed properties
const currentImage = computed(() => {
  const images =
    currentActivity.value?.image_thumbnail ||
    makerCartData.value.image_thumbnail ||
    [];
  return images[currentImageIndex.value] || "/images/placeholder.jpg";
});

const displayImages = computed(() => {
  const images =
    currentActivity.value?.image_thumbnail ||
    makerCartData.value.image_thumbnail ||
    [];
  return images.slice(0, 2);
});

const allImages = computed(() => {
  return (
    currentActivity.value?.image_thumbnail ||
    makerCartData.value.image_thumbnail ||
    []
  );
});

const hasMoreImages = computed(() => {
  const images =
    currentActivity.value?.image_thumbnail ||
    makerCartData.value.image_thumbnail ||
    [];
  return images.length > 2;
});

const openLoginModal = () => {
  showAuthModal.value = true;
};
const handleCloseAuthModal = () => {
  showAuthModal.value = false;
};
const handleLoginSuccess = () => {
  isAuthenticated.value = true;
  showAuthModal.value = false;
  fetchLimitCounter();
};

const checkAuth = async () => {
  const token = await cookiesService.getCookie("authToken");
  isAuthenticated.value = !!token;
};

// Get remaining resource views & already viewed IDs
const fetchLimitCounter = async () => {
  try {
    const res = await makercartActivityService.getLimitCounter();
    if (res?.status === 200) {
      limitResourceLeft.value = 3 - (res.data?.data?.count ?? 0);
      viewedActivityIds.value = res.data?.data?.activity_ids ?? [];
    }
  } catch (e) {
    console.warn("Failed to fetch limit counter", e);
  }
};

const availableGrades = computed(() => {
  const grades = [];

  if (makerCartData.value.upper_elementary_activities?.length > 0) {
    grades.push({
      value: "upper_elementary",
      title: "Upper Elementary",
      text: "Upper Elementary",
    });
  }

  if (makerCartData.value.middle_school_activities?.length > 0) {
    grades.push({
      value: "middle_school",
      title: "Middle School",
      text: "Middle School",
    });
  }

  // Fallback to default grades if no activities are available
  if (grades.length === 0) {
    return [
      {
        value: "upper_elementary",
        title: "Upper Elementary",
        text: "Upper Elementary",
      },
      {
        value: "middle_school",
        title: "Middle School",
        text: "Middle School",
      },
    ];
  }

  return grades;
});

// Computed property for activities based on selected grade
const currentActivities = computed(() => {
  if (
    !makerCartData.value.upper_elementary_activities &&
    !makerCartData.value.middle_school_activities
  ) {
    return [];
  }

  if (selectedGrade.value === "upper_elementary") {
    return makerCartData.value.upper_elementary_activities || [];
  } else if (selectedGrade.value === "middle_school") {
    return makerCartData.value.middle_school_activities || [];
  }

  return [];
});

const currentActivity = computed(() => {
  if (!currentActivities.value.length) return null;
  const activity = currentActivities.value.find(
    (a) => a.id === activeActivityTab.value
  );
  return activity || currentActivities.value[0];
});

const activityOptions = computed(() => {
  return currentActivities.value.map((activity) => ({
    value: activity.id,
    text: activity.makercart_activity,
  }));
});

// Methods
const getMakerCartByIds = async (ids) => {
  try {
    const response = await stemStudioService.getMaterialByIdBulk(ids);
    if (response.status === 200) {
      return response.data.data;
    }
  } catch (error) {
    console.error("Error fetching materials:", error);
  }
  return [];
};

const fetchActivityMaterials = async (activity) => {
  if (!activity?.material_needed?.length) {
    materialsProvided.value = [];
    materialsToProvide.value = [];
    return;
  }

  try {
    loadingMaterials.value = true;
    const materialNeededString = activity.material_needed.join(",");
    const materialData = await getMakerCartByIds(materialNeededString);

    // Reset materials
    materialsProvided.value = [];
    materialsToProvide.value = [];

    if (materialData) {
      materialData.forEach((item) => {
        if (item.includedInMC) {
          materialsProvided.value.push(item.name);
        } else {
          materialsToProvide.value.push(item.name);
        }
      });
    }
  } catch (error) {
    console.error("Error fetching activity materials:", error);
    materialsProvided.value = [];
    materialsToProvide.value = [];
  } finally {
    loadingMaterials.value = false;
  }
};

const fetchMakerCartData = async (id) => {
  if (!id) return;

  try {
    loading.value = true;

    // Get the limit counter and update the UI
    const limitResponse = await makercartActivityService.getLimitCounter();
    if (limitResponse.status === 200) {
      limitResourceLeft.value = 3 - limitResponse.data.data.count;
      viewedActivityIds.value = limitResponse.data.data.activity_ids || [];
    }

    // Fetch signature makercart data from API
    const response =
      await signatureMakercartService.getSignatureMakercartById(id);
    if (response.success) {
      const apiData = response.data;

      // Map API response to expected modal structure
      makerCartData.value = {
        id: apiData.id,
        makercart_activity: apiData.title,
        title: apiData.title,
        subtitle: apiData.subheader,
        theme: apiData.theme,
        series: apiData.series,
        description: apiData.description,
        // Grade-specific activities
        upper_elementary_activities: apiData.upper_elementary_activities || [],
        middle_school_activities: apiData.middle_school_activities || [],
      };

      loading.value = false;
      // Set default selections
      selectedGrade.value =
        availableGrades.value[0]?.value || "upper_elementary";
      selectedProjects.value = 80;
      projectInput.value = 80;
      selectedPoster.value = false;

      // Set initial active activity tab based on first activity of selected grade
      if (currentActivities.value.length > 0) {
        activeActivityTab.value = currentActivities.value[0].id;
        // Fetch materials for the initial activity
        await fetchActivityMaterials(currentActivities.value[0]);
        // Scroll to first tab when modal opens
        scrollToActiveTab();
      }
    } else {
      console.error("Failed to fetch signature makercart data:", response);
      // Set fallback data
      makerCartData.value = {};
    }
  } catch (error) {
    console.error("Error loading signature makercart data:", error);
    // Set fallback data
    makerCartData.value = {};
  } finally {
    loading.value = false;
  }
};

const setCurrentImage = (index) => {
  currentImageIndex.value = index;
};

const previousImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  }
};

const nextImage = () => {
  if (currentImageIndex.value < allImages.value.length - 1) {
    currentImageIndex.value++;
  }
};

const showImageGallery = () => {
  galleryInitialIndex.value = 0;
  showImageGalleryModal.value = true;
};

const openImageGalleryWithIndex = (index) => {
  galleryInitialIndex.value = index || 0;
  showImageGalleryModal.value = true;
};

const closeImageGallery = () => {
  showImageGalleryModal.value = false;
};

const handleImageError = (event) => {
  event.target.src = "/images/placeholder.jpg";
};

const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};

const incrementStudents = () => {
  if (selectedProjects.value < 150) {
    selectedProjects.value = Math.min(selectedProjects.value + 10, 150);
    projectInput.value = selectedProjects.value;
  }
};

const decrementStudents = () => {
  if (selectedProjects.value > 80) {
    selectedProjects.value = Math.max(selectedProjects.value - 10, 80);
    projectInput.value = selectedProjects.value;
  }
};

function handleProjectsInput() {
  if (snapTimer) clearTimeout(snapTimer);
  snapTimer = setTimeout(() => {
    snapProjects();
  }, 400);
}

function snapProjects() {
  let val = Number(projectInput.value);
  if (Number.isNaN(val)) {
    val = 80;
  }
  // Round to nearest multiple of 10
  val = Math.round(val / 10) * 10;
  // Clamp to allowed range
  val = Math.min(150, Math.max(80, val));
  projectInput.value = val;
  selectedProjects.value = val;
}

const handleCartSelection = async (cartValue) => {
  try {
    // Add signature makercart to cart
    const result = await addSignatureMakercart(
      makerCartData.value,
      selectedGrade.value,
      selectedProjects.value,
      selectedPoster.value
    );

    if (result.success) {
      notify?.success("Successfully added to cart!");
      emit("add-to-cart", result.signatureMakercart);
      closeModal();
    } else {
      notify?.error(
        result.error || "Failed to add signature makercart to cart"
      );
    }
  } catch (error) {
    console.error("Error adding signature makercart to cart:", error);
    notify?.error("An error occurred while adding to cart");
  }
};

const shareActivity = () => {
  if (navigator.share) {
    navigator.share({
      title: makerCartData.value.makercart_activity,
      text: makerCartData.value.activity_description,
      url: window.location.href,
    });
  } else {
    // Fallback: copy URL to clipboard
    navigator.clipboard.writeText(window.location.href);
    notify?.plain("Link copied to clipboard!");
  }
};

const viewLessonMaterials = () => {
  if (currentActivity.value?.makercart_id) {
    // Open lesson materials in new tab
    window.open(
      `/lesson-materials/${currentActivity.value.makercart_id}?locale=${localeStore.currentLocale}`,
      "_blank"
    );
  }
};

// Mobile-specific methods
const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 768;
};

const toggleMobileSidebar = () => {
  if (isMobile.value) {
    showMobileSidebar.value = !showMobileSidebar.value;
  }
};

const closeMobileSidebar = () => {
  showMobileSidebar.value = false;
};

// Scroll to active tab in mobile activity selector
const scrollToActiveTab = () => {
  if (!isMobile.value) return;

  nextTick(() => {
    const activeTab = document.querySelector(".mobile-activity-tab.active");
    const tabsContainer = document.querySelector(".mobile-activity-tabs");

    if (activeTab && tabsContainer) {
      const containerRect = tabsContainer.getBoundingClientRect();
      const activeTabRect = activeTab.getBoundingClientRect();

      // Calculate scroll position to center the active tab
      const scrollLeft =
        activeTab.offsetLeft -
        containerRect.width / 2 +
        activeTabRect.width / 2;

      tabsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });
    }
  });
};

const closeModal = () => {
  isVisible.value = false;
  // Reset data to prevent showing stale content
  makerCartData.value = {};
  loading.value = false;
  showDetails.value = false;
  currentImageIndex.value = 0;
  selectedPoster.value = false;
  resetTags();
};

const openModal = async (id) => {
  // Set loading state immediately before showing modal
  loading.value = true;
  isVisible.value = true;

  // Prevent body scroll
  document.body.style.overflow = "hidden";
  // Also lock the root element to avoid parent scrollbars
  document.documentElement.style.overflow = "hidden";

  await checkAuth();
  await fetchLimitCounter();

  if (id) {
    await fetchMakerCartData(id);
  } else {
    // If no ID provided, stop loading
    loading.value = false;
  }
};

// Animation handlers
const onEnter = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};

const onAfterEnter = (el) => {
  el.style.opacity = "1";
  el.style.transform = "scale(1)";
};

const onLeave = (el) => {
  el.style.opacity = "0";
  el.style.transform = "scale(0.9)";
};

const onAfterLeave = () => {
  // Restore body scroll
  document.body.style.overflow = "";
  document.documentElement.style.overflow = "";
  emit("close");
};

// Handle ESC key
const handleEscKey = (event) => {
  if (event.key === "Escape" && isVisible.value) {
    closeModal();
  }
};

// Watchers
watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.makercartId) {
      openModal(props.makercartId);
    } else if (!newVal) {
      closeModal();
    }
  },
  { immediate: true }
);

// Watch for grade changes to reset active activity tab
watch(
  () => selectedGrade.value,
  (newGrade) => {
    if (currentActivities.value.length > 0) {
      activeActivityTab.value = currentActivities.value[0].id;
      // Scroll to first tab when grade changes
      scrollToActiveTab();
      // Reset tags expansion
      resetTags();
    }
  }
);

// Watch for activity changes to reset image index and fetch materials
watch(
  () => activeActivityTab.value,
  async (newActivityId) => {
    currentImageIndex.value = 0;
    resetTags();

    // Find the current activity and fetch its materials
    const activity = currentActivities.value.find(
      (a) => a.id === newActivityId
    );
    if (activity) {
      await fetchActivityMaterials(activity);
    }

    // Scroll to active tab on mobile
    scrollToActiveTab();
  }
);

// Authentication and store watchers
onMounted(async () => {
  // Check authentication
  const token = await cookiesService.getCookie("authToken");
  if (token) {
    isAuthenticated.value = true;
  }

  document.addEventListener("keydown", handleEscKey);
  window.addEventListener("resize", checkScreenSize);
  checkScreenSize(); // Initial check
});

// Watch for changes in the Vuex store user data
watch(
  () => store.state.user,
  (newUserData) => {
    if (newUserData) {
      isAuthenticated.value = true;
    } else {
      isAuthenticated.value = false;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  window.removeEventListener("resize", checkScreenSize);
  document.body.style.overflow = "";
});

// Expose methods for parent component
defineExpose({
  openModal,
  closeModal,
});
</script>

<style scoped src="@/assets/css/catalog-makercart.css"></style>
<style scoped>
.resource-views-banner {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
}

/* Mobile Add to Cart Button */
.mobile-add-to-cart-btn {
  background: var(--primary-blue);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  color: white;
  width: 100%;
  margin: 20px 0;
  transition: background-color 0.2s ease;
}

.mobile-add-to-cart-btn:hover {
  background: #002a4a;
}

/* Mobile Bottom Sheet */
.mobile-sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 19;
}

.mobile-bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: auto;
  max-height: 80vh;
  background: white;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 20;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.mobile-bottom-sheet.show {
  transform: translateY(0);
}

.mobile-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.mobile-bottom-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mobile-bottom-sheet-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
}

.mobile-bottom-sheet-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.mobile-bottom-sheet-close:hover {
  background: #f3f4f6;
}

.mobile-bottom-sheet-close img {
  width: 24px;
  height: 24px;
}

.mobile-bottom-sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
}

.mobile-form-group {
  margin-bottom: 24px;
}

.mobile-form-label-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.mobile-form-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #40444b;
  flex: 1;
}

.mobile-number-input {
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  gap: 12px;
  margin-bottom: 12px;
}

.mobile-number-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #00925e;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-number-btn:hover:not(:disabled) {
  background: #f0f9ff;
}

.mobile-number-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-number-input-field {
  flex: 1;
  width: 100%;
  text-align: center;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  color: var(--black-body-text);
  border: none;
  outline: none;
  background: transparent;
  border-right: 1px solid #6c737f;
  border-left: 1px solid #6c737f;
}

.mobile-checkbox-container {
  margin-top: 8px;
}

.mobile-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-radio-option {
  display: flex;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  gap: 12px;
}

.mobile-radio-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-radio-input {
  display: none;
}

.mobile-radio-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mobile-radio-input:checked + .mobile-radio-custom {
  border-color: var(--primary-blue);
  background: white;
}

.mobile-radio-input:checked + .mobile-radio-custom::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: var(--primary-blue);
  border-radius: 50%;
}

.mobile-radio-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  letter-spacing: 0.08px;
}

.mobile-bottom-sheet-actions {
  padding: 16px 20px 20px 20px;
  background: white;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.mobile-action-divider {
  height: 1px;
  background: #e5e7eb;
  margin-bottom: 16px;
}

.mobile-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Mobile Image Navigation */
.mobile-image-nav {
  position: absolute;
  bottom: 8px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.mobile-nav-btn {
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #00925e;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mobile-nav-btn:hover:not(:disabled) {
  background: #f0f9ff;
  transform: scale(1.05);
}

.mobile-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #d1d5db;
}

.mobile-nav-btn:disabled svg {
  color: #9ca3af;
}

.signature-makercart-mobile-section {
  max-width: 100vw;
  display: none;
}
/* Mobile Activity Selector */
.mobile-activity-selector {
  margin: 16px 0 0 0;
  padding: 0 16px;
}

.mobile-activity-tabs-container {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.mobile-activity-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0 8px 0;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.mobile-activity-tabs::-webkit-scrollbar {
  display: none;
}

.mobile-activity-tab {
  background: white;
  border: 1px solid var(--primary-blue);
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.mobile-activity-tab:hover {
  background: #f9fafb;
  border-color: #00925e;
  color: #00925e;
}

.mobile-activity-tab.active {
  background: var(--primary-blue);
  border-color: var(--primary-blue);
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.3);
}

.mobile-activity-tab.active:hover {
  background: #002a4a;
  border-color: #002a4a;
  color: white;
}

.mobile-activity-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #40444b;
  margin-bottom: 8px;
}

.mobile-section-description {
  background-color: #ededfc;
  font-size: var(--fs-small);
  font-weight: 400;
  padding: 16px;
  color: #3b4648;
  margin: 0 0 16px 0;
  border-radius: 8px;
}

/* Mobile Activity Selector Responsive Styles */
@media (max-width: 480px) {
  .signature-makercart-mobile-section {
    display: block;
  }

  .mobile-activity-selector {
    margin: 12px 0 0 0;
    padding: 0 12px;
  }

  .mobile-activity-tab {
    font-size: 13px;
    padding: 6px 12px;
  }

  .mobile-activity-tabs {
    gap: 6px;
    padding: 2px 0 6px 0;
  }

  .makercart-content-header {
    padding: 0 !important;
  }
}

/* Ensure proper spacing on very small screens */
@media (max-width: 360px) {
  .mobile-activity-selector {
    padding: 0 8px;
  }

  .mobile-activity-tab {
    font-size: 12px;
    padding: 5px 10px;
  }
}

/* Formatted description styles for Airtable rich text content */
.formatted-description :deep(p) {
  margin: 0 0 0.5em 0;
}

.formatted-description :deep(p:last-child) {
  margin-bottom: 0;
}

.formatted-description :deep(strong) {
  font-weight: 600;
}

.formatted-description :deep(em) {
  font-style: italic;
}

.formatted-description :deep(del) {
  text-decoration: line-through;
}

.formatted-description :deep(ul),
.formatted-description :deep(ol) {
  margin: 0.5em 0;
  padding-left: 1.5em;
}

.formatted-description :deep(li) {
  margin-bottom: 0.25em;
}

.formatted-description :deep(a) {
  color: #00925e;
  text-decoration: underline;
}

.formatted-description :deep(a:hover) {
  color: #007a4e;
}

.formatted-description :deep(br) {
  content: "";
  display: block;
  margin-top: 0.25em;
}
</style>
